'use client'

import {
    getPublicConsent,
    PublicConsentData,
    signPublicConsent,
} from '@/lib/api/public/consent-public-api'
import { useEffect, useRef, useState } from 'react'
import {
    MdCheckCircle,
    MdError,
    MdHandshake,
    MdRefresh,
} from 'react-icons/md'
import { useParams } from 'react-router-dom'
import SignatureCanvas from 'react-signature-canvas'

export default function ConsentSignPage() {
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'light')
        return () => {
            // Restaura o tema padrão em vez de remover o atributo: sem `data-theme`,
            // o daisyUI aplica o tema escuro via `:root:not([data-theme])`.
            document.documentElement.setAttribute('data-theme', 'atendimento')
        }
    }, [])
    const { token } = useParams<{ token: string }>()

    const [loading, setLoading] = useState(true)
    const [consent, setConsent] = useState<PublicConsentData | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [signing, setSigning] = useState(false)
    const [signed, setSigned] = useState(false)
    const [signError, setSignError] = useState<string | null>(null)

    const sigCanvas = useRef<SignatureCanvas>(null)

    useEffect(() => {
        if (token) {
            loadConsent()
        }
    }, [token])

    const loadConsent = async () => {
        if (!token) return

        setLoading(true)
        setError(null)

        const response = await getPublicConsent(token)
        if (response.success) {
            setConsent(response.result)
        } else {
            setError(response.error || 'Erro ao carregar consentimento')
        }
        setLoading(false)
    }

    const handleClear = () => {
        sigCanvas.current?.clear()
    }

    const handleSign = async () => {
        if (!token || !sigCanvas.current) return

        if (sigCanvas.current.isEmpty()) {
            setSignError('Por favor, desenhe sua assinatura antes de confirmar.')
            return
        }

        setSignError(null)
        setSigning(true)

        const signatureData = sigCanvas.current.toDataURL('image/png')
        const response = await signPublicConsent(token, signatureData)

        if (response.success) {
            setSigned(true)
        } else {
            setSignError(response.error || 'Erro ao registrar assinatura')
        }

        setSigning(false)
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-base-200 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                    <p className="text-base-content/60">Carregando termo de consentimento...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
                <div className="card bg-base-100 shadow-xl !max-w-md w-full">
                    <div className="card-body items-center text-center">
                        <MdError className="text-6xl text-error mb-2" />
                        <h2 className="card-title text-error">Link inválido ou expirado</h2>
                        <p className="text-base-content/60">{error}</p>
                        <p className="text-sm text-base-content/40 mt-4">
                            Se você precisa assinar um consentimento, solicite um novo link ao profissional responsável.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (signed) {
        return (
            <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
                <div className="card bg-base-100 shadow-xl !max-w-md w-full">
                    <div className="card-body items-center text-center">
                        <MdCheckCircle className="text-6xl text-success mb-2" />
                        <h2 className="card-title text-success">Assinatura Registrada!</h2>
                        <p className="text-base-content/60">
                            Sua assinatura no termo de consentimento foi registrada com sucesso.
                        </p>
                        <p className="text-sm text-base-content/40 mt-2">
                            O profissional responsável será notificado. Você pode fechar esta página.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (!consent) return null

    const companyName = consent.company.tradingName || consent.company.name

    if (consent.alreadySigned) {
        return (
            <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
                <div className="card bg-base-100 shadow-xl !max-w-md w-full">
                    <div className="card-body items-center text-center">
                        <MdCheckCircle className="text-6xl text-success mb-2" />
                        <h2 className="card-title">Consentimento já assinado</h2>
                        <p className="text-base-content/60">
                            Este termo de consentimento já foi assinado
                            {consent.patientSignedAt && (
                                <> em {new Date(consent.patientSignedAt).toLocaleDateString('pt-BR')}</>
                            )}
                            .
                        </p>
                        <div className="mt-4 text-sm text-base-content/40">
                            <p>{companyName}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (consent.revokedAt) {
        return (
            <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
                <div className="card bg-base-100 shadow-xl !max-w-md w-full">
                    <div className="card-body items-center text-center">
                        <MdError className="text-6xl text-error mb-2" />
                        <h2 className="card-title text-error">Consentimento revogado</h2>
                        <p className="text-base-content/60">
                            Este termo de consentimento foi revogado e não pode ser assinado.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-base-200 py-8 px-4">
            <div className="!max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                    <h1 className="text-xl font-bold text-base-content">{companyName}</h1>
                </div>

                {/* Consent Card */}
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        {/* Title */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-secondary/10 rounded-lg">
                                <MdHandshake className="text-2xl text-secondary" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-base-content">
                                    Termo de Consentimento Informado
                                </h2>
                                <p className="text-sm text-base-content/60">
                                    {consent.typeLabel}
                                </p>
                            </div>
                        </div>

                        {/* Patient & Professional */}
                        <div className="flex flex-wrap gap-4 text-sm mb-4">
                            <div>
                                <span className="text-base-content/50">Paciente: </span>
                                <span className="font-medium">{consent.client.name}</span>
                            </div>
                            <div>
                                <span className="text-base-content/50">Profissional: </span>
                                <span className="font-medium">{consent.professional.name}</span>
                            </div>
                        </div>

                        <div className="divider my-2"></div>

                        {/* Content */}
                        <div className="bg-base-200/50 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto">
                            <p className="text-sm text-base-content/80 whitespace-pre-wrap leading-relaxed">
                                {consent.content}
                            </p>
                        </div>

                        {/* Signature Section */}
                        <div className="mb-4">
                            <label className="text-sm font-medium text-base-content mb-2 block">
                                Assinatura do Paciente
                            </label>
                            <div className="border-2 border-base-300 rounded-lg overflow-hidden bg-white">
                                <SignatureCanvas
                                    ref={sigCanvas}
                                    canvasProps={{
                                        className: 'w-full',
                                        style: { height: 200, width: '100%' },
                                    }}
                                    penColor="black"
                                />
                            </div>
                            <div className="flex justify-end mt-2">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="btn btn-ghost btn-sm gap-1"
                                >
                                    <MdRefresh className="text-lg" />
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {signError && (
                            <div className="alert alert-error mb-4">
                                <MdError className="text-xl" />
                                <span>{signError}</span>
                            </div>
                        )}

                        {/* Confirm Button */}
                        <button
                            onClick={handleSign}
                            disabled={signing}
                            className="btn btn-secondary btn-lg w-full gap-2"
                        >
                            {signing ? (
                                <>
                                    <span className="loading loading-spinner loading-sm"></span>
                                    Registrando assinatura...
                                </>
                            ) : (
                                <>
                                    <MdCheckCircle className="text-xl" />
                                    Confirmar Assinatura
                                </>
                            )}
                        </button>

                        <p className="text-xs text-center text-base-content/40 mt-4">
                            Ao confirmar, você declara que leu e concorda com o conteúdo do termo acima.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
